/**
 * Client half of the session / idle-timeout system (server half:
 * server/src/lib/sessions.ts). Three jobs:
 *
 * 1. PROACTIVE REFRESH — while the user is active, silently renew the short
 *    access token via POST /api/auth/refresh (the httpOnly cookie authenticates
 *    it). Refreshing is ACTIVITY-GATED: an idle tab stops refreshing, which is
 *    what lets the server's sliding idle window actually expire.
 * 2. IDLE WARNING — track real user activity (shared across tabs via
 *    localStorage) and surface a countdown modal shortly before the idle
 *    deadline; "Stay signed in" refreshes and slides the window.
 * 3. IDLE SIGN-OUT — past the deadline, sign out locally (the server has
 *    already stopped honoring the session) and flag the login page to explain
 *    why the user is back there.
 *
 * The idle/warn durations are POLICY, and policy lives on the server
 * (sessionConfig.ts): every refresh response carries the authoritative values;
 * the constants below are only pre-first-refresh fallbacks that mirror them.
 *
 * Impersonation note: while a super admin impersonates a user, the manager
 * pauses entirely — the refresh cookie belongs to the OPERATOR's session, and a
 * background refresh would silently swap the impersonated token back to the
 * operator's own. Impersonation tokens keep their hard 2h server-side expiry.
 */
import axios from 'axios';
import { create } from 'zustand';
import { useAuthStore } from '../store/useAuthStore';

// Fallbacks until the first refresh response delivers the server's values.
let idleTimeoutMs = 60 * 60 * 1000;
let warnBeforeMs = 2 * 60 * 1000;

const ACTIVITY_KEY = 'erm-last-activity';
/** Read by LoginPage to explain the bounce ("signed out after inactivity"). */
export const IDLE_SIGNOUT_FLAG = 'avium-idle-signout';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
const ACTIVITY_WRITE_THROTTLE_MS = 5_000;
const CHECK_INTERVAL_MS = 15_000;
// Well under the 15-minute access-token TTL so an active user's token never
// expires mid-flight (and the 401-retry path in api.ts stays a rare fallback).
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/** Idle-warning UI state; SessionIdleModal renders from this. `deadline` is the
 *  epoch-ms moment the user will be signed out (null = no warning showing). */
export const useIdleWarningStore = create<{ deadline: number | null }>(() => ({ deadline: null }));

let memoryLastActivity = Date.now();
let lastActivityWrite = 0;
let lastRefreshAt = 0;
let refreshPromise: Promise<string | null> | null = null;
let checkTimer: ReturnType<typeof setInterval> | null = null;
let started = false;

function recordActivity(): void {
  const now = Date.now();
  memoryLastActivity = now;
  if (now - lastActivityWrite >= ACTIVITY_WRITE_THROTTLE_MS) {
    lastActivityWrite = now;
    try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* storage full/blocked — memory copy still works */ }
  }
}

/** Latest activity across ALL tabs (localStorage) and this tab (memory). */
function lastActivityAt(): number {
  let stored = 0;
  try { stored = Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { /* ignore */ }
  return Math.max(memoryLastActivity, stored);
}

/**
 * Renew the access token from the session cookie. Shared in-flight promise so
 * concurrent callers (the manager's timer + several 401 retries) produce ONE
 * network call. Resolves null when the session is dead — callers decide what
 * that means for them.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    // Raw axios, NOT the api instance: the refresh call must never recurse
    // through the 401-retry interceptor that calls it.
    refreshPromise = axios
      .post('/api/auth/refresh')
      .then((res) => {
        const { token, session } = res.data as {
          token: string;
          session?: { idleTimeoutMs?: number; warnBeforeMs?: number };
        };
        if (session?.idleTimeoutMs) idleTimeoutMs = session.idleTimeoutMs;
        if (session?.warnBeforeMs) warnBeforeMs = session.warnBeforeMs;
        lastRefreshAt = Date.now();
        useAuthStore.getState().setToken(token);
        return token;
      })
      .catch(() => null)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

function signOutForIdle(): void {
  try { sessionStorage.setItem(IDLE_SIGNOUT_FLAG, '1'); } catch { /* ignore */ }
  useAuthStore.getState().logout();
  window.location.href = '/login';
}

function tick(): void {
  const { token, isImpersonating } = useAuthStore.getState();
  if (!token || isImpersonating) {
    if (useIdleWarningStore.getState().deadline !== null) useIdleWarningStore.setState({ deadline: null });
    return;
  }

  const idleFor = Date.now() - lastActivityAt();

  if (idleFor >= idleTimeoutMs) {
    signOutForIdle();
    return;
  }

  if (idleFor >= idleTimeoutMs - warnBeforeMs) {
    // Inside the warning window — show/refresh the countdown, and stop
    // refreshing so the server-side window is allowed to expire.
    useIdleWarningStore.setState({ deadline: lastActivityAt() + idleTimeoutMs });
    return;
  }

  if (useIdleWarningStore.getState().deadline !== null) useIdleWarningStore.setState({ deadline: null });

  // Active user, token aging: renew. The activity gate (was there input since
  // the last refresh?) is what makes a forgotten background tab go idle.
  if (Date.now() - lastRefreshAt >= REFRESH_INTERVAL_MS && lastActivityAt() > lastRefreshAt) {
    void refreshAccessToken();
  }
}

/** "I'm still here" — counts as activity and slides the server window now. */
export function staySignedIn(): void {
  lastActivityWrite = 0; // force the cross-tab write through the throttle
  recordActivity();
  useIdleWarningStore.setState({ deadline: null });
  void refreshAccessToken();
}

export function startSessionManager(): void {
  if (started) return;
  started = true;
  recordActivity();
  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, recordActivity, { passive: true }));
  document.addEventListener('visibilitychange', onVisibility);
  checkTimer = setInterval(tick, CHECK_INTERVAL_MS);

  // Immediate sync: after a reload the stored access token may be near (or
  // past) expiry while the session cookie is alive and well — refresh now to
  // start clean and pick up the server's current idle policy. Harmless no-op
  // failure for legacy pre-session logins (no cookie yet).
  const { token, isImpersonating } = useAuthStore.getState();
  if (token && !isImpersonating) void refreshAccessToken();
}

function onVisibility(): void {
  // Returning to the tab is activity ONLY if the session hasn't already idled
  // out — check first, so reopening a stale laptop signs out instead of reviving.
  if (document.visibilityState !== 'visible') return;
  tick();
  if (useAuthStore.getState().token) recordActivity();
}

export function stopSessionManager(): void {
  if (!started) return;
  started = false;
  ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, recordActivity));
  document.removeEventListener('visibilitychange', onVisibility);
  if (checkTimer) { clearInterval(checkTimer); checkTimer = null; }
  useIdleWarningStore.setState({ deadline: null });
}
